import React, { useState } from 'react';
import { Key, Copy, Trash2, RefreshCw, Shield } from 'lucide-react';

const TokenGeneratorTool = () => {
  const [tokenType, setTokenType] = useState('jwt-like');
  const [count, setCount] = useState(5);
  const [tokens, setTokens] = useState([]);
  const [copied, setCopied] = useState('');

  const generateRandomBase64 = (length) => {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode.apply(null, array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };

  const generateHex = (length) => {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
  };

  const generateToken = () => {
    switch (tokenType) {
      case 'jwt-like':
        // Generate JWT-like structure (not valid JWT, just similar format)
        const header = generateRandomBase64(16);
        const payload = generateRandomBase64(32);
        const signature = generateRandomBase64(24);
        return `${header}.${payload}.${signature}`;
      
      case 'bearer':
        return `Bearer ${generateRandomBase64(32)}`;
      
      case 'api-key':
        const prefix = ['sk_live_', 'pk_live_', 'api_', 'key_'][Math.floor(Math.random() * 4)];
        return `${prefix}${generateRandomBase64(32)}`;
      
      case 'oauth':
        return generateRandomBase64(40);
      
      case 'session':
        return `sess_${generateRandomBase64(24)}`;
      
      case 'refresh':
        return `refresh_${generateRandomBase64(48)}`;
      
      case 'hex-256':
        return generateHex(32); // 256 bits
      
      case 'hex-512':
        return generateHex(64); // 512 bits
      
      case 'uuid-like':
        const parts = [
          generateHex(4),
          generateHex(2),
          generateHex(2),
          generateHex(2),
          generateHex(6)
        ];
        return parts.join('-');
      
      default:
        return generateRandomBase64(32);
    }
  };

  const generateTokens = () => {
    const newTokens = [];
    for (let i = 0; i < count; i++) {
      newTokens.push({
        value: generateToken(),
        createdAt: new Date().toISOString()
      });
    }
    setTokens(newTokens);
  };

  const copyToken = (token, index) => {
    navigator.clipboard.writeText(token);
    setCopied(index);
    setTimeout(() => setCopied(''), 1500);
  };

  const copyAll = () => {
    navigator.clipboard.writeText(tokens.map(t => t.value).join('\n'));
    setCopied('all');
    setTimeout(() => setCopied(''), 2000);
  };

  const tokenTypes = {
    'jwt-like': { name: 'JWT-like Token', desc: 'Three-part token similar to JWT structure' },
    'bearer': { name: 'Bearer Token', desc: 'Authorization header format token' },
    'api-key': { name: 'API Key', desc: 'Prefixed API key format' },
    'oauth': { name: 'OAuth Token', desc: 'OAuth 2.0 style access token' },
    'session': { name: 'Session Token', desc: 'Session identifier token' },
    'refresh': { name: 'Refresh Token', desc: 'Long-lived refresh token' },
    'hex-256': { name: 'Hex 256-bit', desc: '64-character hexadecimal token' },
    'hex-512': { name: 'Hex 512-bit', desc: '128-character hexadecimal token' },
    'uuid-like': { name: 'UUID-like', desc: 'UUID format random token' }
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-yellow-900/30 border border-yellow-700 rounded-lg">
        <p className="text-sm text-yellow-300">
          These tokens are randomly generated for testing and development purposes. 
          They are NOT cryptographically signed and should not be used as real authentication tokens.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Token Type</label>
          <select
            value={tokenType}
            onChange={(e) => setTokenType(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          >
            {Object.entries(tokenTypes).map(([key, info]) => (
              <option key={key} value={key}>{info.name}</option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">{tokenTypes[tokenType].desc}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Count</label>
          <input
            type="number"
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(20, parseInt(e.target.value) || 5)))}
            min="1"
            max="20"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          />
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={generateTokens}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        >
          <Key size={18} />
          Generate Tokens
        </button>
        <button
          onClick={generateTokens}
          disabled={tokens.length === 0}
          className="flex items-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 text-white rounded-lg transition-colors"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {tokens.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-300">Generated Tokens</label>
            <button
              onClick={copyAll}
              className={`text-xs px-3 py-1 rounded transition-colors ${
                copied === 'all' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {copied === 'all' ? 'Copied!' : 'Copy All'}
            </button>
          </div>
          <div className="space-y-2">
            {tokens.map((token, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <code className="font-mono text-green-400 text-xs break-all mr-2">{token.value}</code>
                <button
                  onClick={() => copyToken(token.value, i)}
                  className={`p-2 rounded transition-colors flex-shrink-0 ${
                    copied === i ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <Copy size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Token Format Examples</h3>
        <div className="grid md:grid-cols-2 gap-3 text-xs font-mono">
          <div className="p-2 bg-gray-900 rounded">
            <span className="text-gray-500">JWT-like:</span>
            <div className="text-green-400 truncate">xxx.yyyyy.zzz</div>
          </div>
          <div className="p-2 bg-gray-900 rounded">
            <span className="text-gray-500">API Key:</span>
            <div className="text-green-400 truncate">sk_live_xxxx...</div>
          </div>
          <div className="p-2 bg-gray-900 rounded">
            <span className="text-gray-500">Session:</span>
            <div className="text-green-400 truncate">sess_xxxx...</div>
          </div>
          <div className="p-2 bg-gray-900 rounded">
            <span className="text-gray-500">UUID-like:</span>
            <div className="text-green-400 truncate">xxxx-xx-xx-xx-xxxxxx</div>
          </div>
        </div>
      </div>

      <button
        onClick={() => setTokens([])}
        disabled={tokens.length === 0}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
      >
        <Trash2 size={16} />
        Clear
      </button>
    </div>
  );
};

export default TokenGeneratorTool;
